import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Users, CalendarRange, LogOut, Plus, Trash2, BellRing, X } from "lucide-react";
import { api, errMsg } from "./api";

const fmt = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; const hh = ((h + 11) % 12) + 1; return `${hh}:${String(m).padStart(2, "0")} ${ap}`; };

export default function AdminDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("clients");
  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <header className="sticky top-0 z-30 border-b border-[var(--cream-3)] bg-[var(--cream)]/95 backdrop-blur">
        <div className="flex items-center justify-between px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-2.5">
            <img src="/nova-logo.png" alt="" className="h-8 w-auto" />
            <div className="leading-none">
              <p className="font-serif-display text-base text-[var(--ink)]">N Murthy <span className="text-[var(--gold)]">Groups</span></p>
              <p className="text-[9px] uppercase tracking-[0.3em] text-[var(--ink-soft)]">Community Admin</p>
            </div>
          </div>
          <button onClick={onLogout} className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="admin-logout">
            <LogOut size={15} /> Exit
          </button>
        </div>
        <div className="flex gap-2 px-5 pb-3 md:px-8">
          {[["clients", Users, "Clients"], ["bookings", CalendarRange, "Bookings"]].map(([id, Icon, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex items-center gap-2 rounded-sm px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${tab === id ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-[var(--cream-2)] text-[var(--ink-soft)]"}`}
              data-testid={`admin-tab-${id}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-8 md:px-8">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            {tab === "clients" ? <ClientsTab /> : <BookingsTab />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

const empty = { name: "", email: "", password: "", phone: "", plot_number: "", plot_size: "", payment_due_date: "" };

function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [reminding, setReminding] = useState(false);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.plot_number) { toast.error("Name, email, password and plot number are required."); return; }
    setSaving(true);
    try {
      await api.post("/clients", { ...form, payment_due_date: form.payment_due_date || null });
      toast.success("Client account created & welcome email sent.");
      setForm(empty); setShow(false); load();
    } catch (err) { toast.error(errMsg(err, "Could not create client")); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this client's access?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Client removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const sendReminders = async () => {
    setReminding(true);
    try { const { data } = await api.post("/reminders/send"); toast.success(`Reminders sent: ${data.sent}`); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setReminding(false); }
  };

  const inp = "w-full rounded-sm border border-[var(--cream-3)] px-3 py-2.5 text-sm outline-none focus:border-[var(--gold)]";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Members</p>
          <h1 className="mt-1 font-serif-display text-3xl text-[var(--ink)]">Clients <span className="text-[var(--ink-soft)]">({clients.length})</span></h1>
        </div>
        <div className="flex gap-2">
          <button onClick={sendReminders} disabled={reminding} className="flex items-center gap-2 rounded-sm border border-[var(--cream-3)] bg-white px-4 py-2.5 text-xs uppercase tracking-widest text-[var(--ink)] hover:border-[var(--gold)] disabled:opacity-60" data-testid="send-reminders-btn">
            <BellRing size={15} /> {reminding ? "Sending…" : "Send Reminders"}
          </button>
          <button onClick={() => setShow(true)} className="flex items-center gap-2 rounded-sm bg-[var(--gold)] px-4 py-2.5 text-xs uppercase tracking-widest text-[#1C2427] hover:-translate-y-0.5" data-testid="add-client-btn">
            <Plus size={15} /> Add Client
          </button>
        </div>
      </div>

      {clients.length === 0 ? (
        <p className="text-[var(--ink-soft)]" data-testid="no-clients">No clients yet. Click "Add Client" to create the first account.</p>
      ) : (
        <div className="space-y-3" data-testid="clients-list">
          {clients.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border border-[var(--cream-3)] bg-white p-4" data-testid={`client-${c.id}`}>
              <div>
                <p className="font-serif-display text-lg text-[var(--ink)]">{c.name} <span className="ml-2 text-sm text-[var(--gold-hover)]">Plot {c.plot_number}</span></p>
                <p className="text-sm text-[var(--ink-soft)]">{c.email} · {c.phone || "no phone"}</p>
                <p className="mt-1 text-xs text-[var(--ink-soft)]">
                  Fee: <span className={c.payment?.status === "paid" ? "text-emerald-600" : "text-amber-600"}>{c.payment?.status === "paid" ? "Paid" : "Due"}</span> · {c.payment?.due_date || "—"}
                </p>
              </div>
              <button onClick={() => del(c.id)} className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100" data-testid={`delete-client-${c.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {show && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onClick={() => setShow(false)} data-testid="add-client-modal">
            <motion.form initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()} onSubmit={create}
              className="w-full max-w-md space-y-4 rounded-t-xl bg-[var(--cream)] p-6 sm:rounded-lg">
              <div className="flex items-center justify-between">
                <h2 className="font-serif-display text-2xl text-[var(--ink)]">New Client</h2>
                <button type="button" onClick={() => setShow(false)}><X size={20} className="text-[var(--ink-soft)]" /></button>
              </div>
              <input className={inp} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="client-name" />
              <input className={inp} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="client-email" />
              <input className={inp} placeholder="Temp password *" value={form.password} onChange={set("password")} data-testid="client-password" />
              <input className={inp} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="client-phone" />
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="Plot number *" value={form.plot_number} onChange={set("plot_number")} data-testid="client-plot-number" />
                <input className={inp} placeholder="Plot size (30x40)" value={form.plot_size} onChange={set("plot_size")} data-testid="client-plot-size" />
              </div>
              <label className="block">
                <span className="mb-1 block text-xs uppercase tracking-widest text-[var(--ink-soft)]">Payment due date (optional, defaults +1yr)</span>
                <input className={inp} type="date" value={form.payment_due_date} onChange={set("payment_due_date")} data-testid="client-due-date" />
              </label>
              <button type="submit" disabled={saving} className="w-full rounded-sm bg-[var(--gold)] py-3.5 text-xs uppercase tracking-[0.25em] text-[#1C2427] disabled:opacity-60" data-testid="save-client-btn">
                {saving ? "Creating…" : "Create Account"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BookingsTab() {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState("");
  const load = () => api.get(`/bookings${date ? `?date=${date}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-[var(--gold)]">Facility Usage</p>
          <h1 className="mt-1 font-serif-display text-3xl text-[var(--ink)]">Bookings</h1>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="rounded-sm border border-[var(--cream-3)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--gold)]" data-testid="admin-booking-date" />
      </div>
      {items.length === 0 ? (
        <p className="text-[var(--ink-soft)]" data-testid="no-admin-bookings">No bookings{date ? ` on ${date}` : ""}.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-bookings-list">
          {items.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-lg border border-[var(--cream-3)] bg-white p-4">
              <div>
                <p className="font-serif-display text-lg text-[var(--ink)]">{b.facility}</p>
                <p className="text-sm text-[var(--ink-soft)]">{b.date} · {fmt(b.start_time)} – {fmt(b.end_time)}</p>
              </div>
              <span className="text-sm text-[var(--ink)]">{b.user_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
